// AiUsageRateTable — pen H-09, read-only: the versioned USD rate table
// (prices per million tokens per model), the calls each model made in the
// period, and a warning for calls on a model the table does not price
// ("Show calls" opens them). Versions are listed newest first; every call
// is priced with the version valid when it started.

export { AiUsageRateTable };

import type { AiUsageRates } from "@bilda/app/agentCallAdmin";
import { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { formatInt } from "../../app/aiUsage/format.js";
import { type AiUsageRoute, withFilter } from "../../app/aiUsage/route.js";
import { Icon } from "../Icon/Icon.js";
import type { AiUsageRead } from "./AiUsagePage.js";
import { LoadFailed, TableSkeleton } from "./AiUsageStates.js";

type Props = {
    readonly route: AiUsageRoute;
    readonly rates: AiUsageRead<AiUsageRates>;
    readonly onNavigate: (route: AiUsageRoute) => void;
    readonly onRetry: () => void;
};

const AiUsageRateTable = ({ route, rates, onNavigate, onRetry }: Props) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const r = Remote.match<AiUsageRates, unknown, AiUsageRates | undefined>({
        notAsked: () => undefined,
        loading: () => undefined,
        reloading: (prev) => prev,
        loaded: (v) => v,
        failed: () => undefined,
    })(rates);
    if (rates._tag === "Remote.Failed") return <LoadFailed onRetry={onRetry} />;
    if (r === undefined) return <TableSkeleton />;
    const usd = new Intl.NumberFormat(loc, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
    const day = new Intl.DateTimeFormat(loc, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
    const versions = [...r.versions].sort((a, b) => b.validFrom - a.validFrom);
    const callsOf = (modelId: string): number =>
        r.models.find((m) => m.modelId === modelId)?.calls ?? 0;
    const unpriced = r.models.filter((m) => m.unpricedCalls > 0);
    return (
        <div className="ai-usage__rates">
            <p className="ai-usage__lede">
                {t`Read-only for now. Prices are set in the code's rate table; every call is priced with the version valid at the time of the call.`}
            </p>
            {unpriced.map((m) => (
                <p key={m.modelId} className="ai-usage__notice" role="status">
                    <Icon name="warning" size="sm" />
                    <span>
                        {t`${formatInt(loc, m.unpricedCalls)} calls used a model that isn't in this table (${m.modelId}). Their cost is shown as unknown until a rate is added.`}
                    </span>
                    <button
                        type="button"
                        className="ai-usage__link"
                        onClick={() =>
                            onNavigate(
                                withFilter(
                                    withFilter(
                                        { ...route, tab: "calls" },
                                        "model",
                                        m.modelId,
                                    ),
                                    "cost",
                                    "unpriced",
                                ),
                            )
                        }
                    >
                        {t`Show calls`}
                        <Icon name="arrow-right" size="xs" />
                    </button>
                </p>
            ))}
            {versions.map((v, i) => (
                <section key={v.version} className="ai-usage__card">
                    <div className="ai-usage__card-head">
                        <h2 className="ai-usage__card-title">
                            {i === 0
                                ? t`v${v.version} · from ${day.format(new Date(v.validFrom))} · current`
                                : t`v${v.version} · from ${day.format(new Date(v.validFrom))}`}
                        </h2>
                    </div>
                    <table className="ai-usage__table">
                        <thead>
                            <tr>
                                <th scope="col">{t`Model`}</th>
                                <th
                                    scope="col"
                                    className="ai-usage__num"
                                >{t`Input`}</th>
                                <th
                                    scope="col"
                                    className="ai-usage__num"
                                >{t`Output`}</th>
                                <th
                                    scope="col"
                                    className="ai-usage__num"
                                >{t`Cache read`}</th>
                                <th
                                    scope="col"
                                    className="ai-usage__num"
                                >{t`Cache write`}</th>
                                {i === 0 ? (
                                    <th scope="col" className="ai-usage__num">
                                        {t`Calls this period`}
                                    </th>
                                ) : null}
                            </tr>
                        </thead>
                        <tbody>
                            {v.rates.map((rate) => (
                                <tr key={rate.modelId}>
                                    <td>
                                        <code>{rate.modelId}</code>
                                    </td>
                                    <td className="ai-usage__num">
                                        {usd.format(rate.inputPerMtok)}
                                    </td>
                                    <td className="ai-usage__num">
                                        {usd.format(rate.outputPerMtok)}
                                    </td>
                                    <td className="ai-usage__num">
                                        {usd.format(rate.cacheReadPerMtok)}
                                    </td>
                                    <td className="ai-usage__num">
                                        {usd.format(rate.cacheWritePerMtok)}
                                    </td>
                                    {i === 0 ? (
                                        <td className="ai-usage__num">
                                            {formatInt(
                                                loc,
                                                callsOf(rate.modelId),
                                            )}
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            ))}
            <p className="ai-usage__muted">{t`Prices per million tokens, USD.`}</p>
        </div>
    );
};
